import { useEffect, useCallback } from 'react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import type { BoxConfirmDialogProps } from './types'

export function BoxConfirmDialog({ isOpen, onClose, onConfirm, boxNumber }: BoxConfirmDialogProps) {
  // Handle Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => {
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [isOpen, handleKeyDown])

  if (!isOpen) return null

  return (
    <div
      data-testid="box-confirm-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 border border-gray-700"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
      >
        {/* Dialog Header */}
        <div className="flex items-start gap-4 p-6 pb-4">
          <div className="flex-shrink-0">
            <ExclamationTriangleIcon className="w-6 h-6 text-yellow-400" />
          </div>
          <div className="flex-1">
            <h3 id="dialog-title" className="text-lg font-semibold text-gray-100">
              Remove Box {boxNumber}?
            </h3>
            <p id="dialog-description" className="mt-2 text-sm text-gray-400">
              This box contains content. Are you sure you want to remove it? This action cannot be undone.
            </p>
          </div>
        </div>

        {/* Dialog Actions */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-900/50">
          <button
            onClick={onClose}
            data-testid="dialog-cancel-button"
            className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600
                     rounded-md transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            data-testid="dialog-confirm-button"
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700
                     rounded-md transition-colors duration-200"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  )
}
